import { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import { Columns2, Merge, Rows2, Save, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DesignCanvas } from "@/components/designer/DesignCanvas";
import { confirmSheet, promptSheet } from "@/components/ui/action-sheet";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLines } from "@/lib/components";
import { SEC_TYPES, fitPieces, get, layout, merge, minOf, patchAt, resize, scale, setSize, sizeGap, split, stuckSlider } from "@/lib/design";
import { NO_MOVING } from "@/lib/sliding";
import { AddPieces, SelectedPiece } from "@/components/designer/PiecePanel";
import { GridPanel } from "@/components/designer/GridPanel";
import { HUNG } from "@/lib/design";
import { defaultColor } from "@/lib/defaults";
import { PriceBox } from "@/components/designer/PriceBox";

const Design3D = lazy(() => import("@/components/designer/Design3D"));
const webgl = () => import("@/lib/model3d").then((m) => m.hasWebGL());

// Saves the 3D snapshot (outside, closed) for the PDF; "" when the device can't draw 3D.
async function saveSnapshot(d, shapes) {
  try {
    const m = await import("@/lib/model3d");
    if (!m.hasWebGL()) return "";
    const r = await api.post("/design-snapshots", { image: m.snapshot(d, shapes) });
    return r.data.path;
  } catch {
    return "";
  }
}

function ViewToggle({ view, setView }) {
  return (
    <div className="absolute right-4 top-4 z-10 flex rounded-full bg-[#E9E9EB] p-0.5" data-testid="design-view-toggle">
      {[["2d", "2D"], ["3d", "3D"]].map(([k, l]) => (
        <button key={k} data-testid={`design-view-${k}`} onClick={() => setView(k)}
          className={`h-8 rounded-full px-4 text-[13px] font-semibold transition-colors ${view === k ? "bg-white text-black shadow-sm" : "text-[#6D6D72]"}`}>{l}</button>
      ))}
    </div>
  );
}

const inp = "h-9 w-full rounded-lg border border-slate-800 bg-white px-2 text-sm text-slate-100 outline-none focus:border-amber-500/70";
const small = "h-7 w-14 rounded-lg border border-slate-800 bg-white px-1.5 text-sm text-slate-100 outline-none focus:border-amber-500/70";
const btn = "inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-slate-700 px-3 text-xs font-semibold text-slate-200 transition-colors hover:border-amber-500 disabled:opacity-40";
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const Lab = ({ t, children }) => <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">{t}<div className="mt-1 normal-case tracking-normal">{children}</div></label>;

function useDesignPrice(design) {
  const { quoteId } = useLines();
  const [p, setP] = useState(null);
  const t = useRef(null);
  useEffect(() => {
    clearTimeout(t.current);
    t.current = setTimeout(() => api.post("/designs/price", { design, ...(quoteId ? { quote_id: quoteId } : {}) }).then((r) => setP(r.data)).catch((e) => setP({ error: errorMessage(e) })), 120);
    return () => clearTimeout(t.current);
  }, [design, quoteId]);
  return p;
}

function Templates({ design, onLoad }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [name, setName] = useState("");
  const [size, setSize_] = useState({});
  const { user } = useAuth();
  const seq = useRef(0);
  const load = () => { const n = ++seq.current; return api.get("/design-templates", { params: { q } }).then((r) => { if (n === seq.current) setList(r.data); }).catch(() => {}); };
  useEffect(() => { load(); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    if (!name.trim()) return toast.error("Name the template");
    try { await api.post("/design-templates", { name, design }); toast.success("Template saved"); setName(""); load(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const act = async (fn) => { try { await fn(); load(); } catch (e) { toast.error(errorMessage(e)); } };
  return (
    <div className="space-y-3" data-testid="design-templates">
      <div className="flex gap-2">
        <input data-testid="template-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name" className={inp} />
        <button data-testid="template-save-button" onClick={save} className={btn}><Save className="h-3.5 w-3.5" /> Save</button>
      </div>
      <label className="relative block"><Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-500" />
        <input data-testid="template-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates" className={`${inp} pl-8`} /></label>
      <div className="max-h-64 space-y-2 overflow-y-auto">
        {list.map((t, i) => {
          const s = size[t.id] || { w: t.design.w, h: t.design.h };
          return (
            <div key={t.id} data-testid={`template-row-${i}`} className="rounded-lg border border-slate-800 p-2">
              <p className="text-sm font-medium text-slate-100">{t.name}</p>
              <p className="text-[11px] text-slate-500">{t.summary}</p>
              <div className="mt-1 flex items-center gap-1">
                <input data-testid={`template-w-${i}`} value={s.w} onChange={(e) => setSize_({ ...size, [t.id]: { ...s, w: e.target.value } })} className={`${small} w-16`} />
                <span className="text-xs text-slate-500">×</span>
                <input data-testid={`template-h-${i}`} value={s.h} onChange={(e) => setSize_({ ...size, [t.id]: { ...s, h: e.target.value } })} className={`${small} w-16`} />
                <button data-testid={`template-use-${i}`} onClick={() => onLoad(scale(t.design, Number(s.w) || t.design.w, Number(s.h) || t.design.h))} className={`${btn} h-7`}>Start</button>
                {(user?.role === "owner" || t.created_by === user?.id) && <>
                <button data-testid={`template-rename-${i}`} onClick={async () => { const n = await promptSheet({ title: "Rename template", input: t.name, confirm: "Rename", required: true }); if (n) act(() => api.put(`/design-templates/${t.id}`, { name: n })); }} className={`${btn} h-7`}>Rename</button>
                <button aria-label="Delete template" data-testid={`template-delete-${i}`} onClick={async () => (await confirmSheet({ title: `Delete template "${t.name}"?`, confirm: "Delete Template", destructive: true })) && act(() => api.delete(`/design-templates/${t.id}`))} className="p-1 text-slate-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </>}
              </div>
            </div>
          );
        })}
        {!list.length && <p className="text-xs text-slate-500">No templates yet.</p>}
      </div>
    </div>
  );
}

function SectionPanel({ d, path, set, screens, onMsg }) {
  const n = get(d.root, path);
  const unitPath = path.slice(0, path.findIndex((_, i) => get(d.root, path.slice(0, i + 1)).t === "unit") + 1);
  const isDoor = get(d.root, unitPath)?.kind === "door";
  const bif = !!get(d.root, unitPath)?.bifold;  // bi-fold piece: panels, fold and smart lock live on the piece panel
  const parentPath = path.slice(0, -1);
  const parent = parentPath.length ? get(d.root, parentPath) : null;
  const L = layout(d);
  const r = L.secs.find((s) => s.path.join() === path.join());
  const p = (x) => set(patchAt(d, path, x));
  const doSplit = (dir) => { const [nd, bad] = split(d, path, dir); if (bad) onMsg("Minimum section 12 in"); else set(nd); };
  const inSplit = parent && parent.t === "split";
  const sizeVal = inSplit ? (parent.dir === "cols" ? r?.w : r?.h) : null;
  return (
    <div className="space-y-3" data-testid="design-section-panel">
      <div className="grid grid-cols-2 gap-2">
        {!bif && <Lab t="Section type">
          <select data-testid="section-type-select" value={n.type} onChange={(e) => p({ type: e.target.value })} className={inp}>
            {SEC_TYPES.filter(([k]) => k !== "door" || isDoor).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Lab>}
        {!bif && ["casement", "tilt_turn", "door"].includes(n.type) && (
          <Lab t="Hinge side"><select data-testid="section-hinge-select" value={n.hinge} onChange={(e) => p({ hinge: e.target.value })} className={inp}><option value="left">Left</option><option value="right">Right</option></select></Lab>
        )}
        {!bif && n.type === "door" && <Lab t="Swing"><select data-testid="section-swing-select" value={n.swing} onChange={(e) => p({ swing: e.target.value })} className={inp}><option value="in">In</option><option value="out">Out</option></select></Lab>}
        {bif && <p data-testid="section-bifold-note" className="col-span-2 text-xs text-slate-500">Bi-fold piece: set panels, fold and smart lock on the piece above.</p>}
        {!bif && n.type === "sliding" && <Lab t="Slides"><select data-testid="section-slide-select" value={n.slide} onChange={(e) => p({ slide: e.target.value })} className={inp}><option value="right">To the right</option><option value="left">To the left</option></select></Lab>}
        {HUNG.includes(n.type) && (
          <Lab t="Top sash height (in)">
            <input data-testid="section-top-sash-input" inputMode="decimal" value={n.top_h ?? ""} placeholder={`${(r.h / 2).toFixed(1)} (equal)`}
              onChange={(e) => p({ top_h: e.target.value === "" ? null : Number(e.target.value) || null })} className={inp} />
          </Lab>
        )}
        {HUNG.includes(n.type) && n.grid && (
          <Lab t="Grids on">
            <select data-testid="section-grid-on-select" value={n.grid_on || "both"} onChange={(e) => p({ grid_on: e.target.value })} className={inp}>
              <option value="both">Both sashes</option><option value="top">Top sash only</option><option value="bottom">Bottom sash only</option>
            </select>
          </Lab>
        )}
        {inSplit && (
          <Lab t={parent.dir === "cols" ? "Width (in)" : "Height (in)"}>
            <input key={`${path.join()}-${sizeVal}`} data-testid="section-size-input" defaultValue={Math.round(sizeVal * 8) / 8} className={inp}
              onBlur={(e) => { const [nd, c] = setSize(d, parentPath, path[path.length - 1], Number(e.target.value) || sizeVal); if (c) onMsg("Minimum section 12 in"); set(nd); }} />
          </Lab>
        )}
      </div>
      <GridPanel n={n} r={r} p={p} />
      <div className="flex flex-wrap items-center gap-2">
        {n.type !== "fixed" && screens.length > 0 && (
          <select data-testid="section-screen-select" value={n.screen_id || ""} onChange={(e) => p({ screen_id: e.target.value || null })} className={`${inp} h-8`}>
            <option value="">No screen</option>{screens.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <button data-testid="section-split-cols" onClick={() => doSplit("cols")} className={btn}><Columns2 className="h-3.5 w-3.5" /> Split columns</button>
        <button data-testid="section-split-rows" onClick={() => doSplit("rows")} className={btn}><Rows2 className="h-3.5 w-3.5" /> Split rows</button>
        <button data-testid="section-merge" disabled={!inSplit} onClick={() => set(merge(d, path))} className={btn}><Merge className="h-3.5 w-3.5" /> Merge</button>
      </div>
    </div>
  );
}

function UnitPanel({ d, path, set, shapes }) {
  const L = layout(d);
  const u = [...L.units].reverse().find((x) => path.join().startsWith(x.path.join()) && path.length > x.path.length) || L.units[0];
  return (
    <div className="space-y-3" data-testid="design-unit-panel">
      <div className="grid grid-cols-2 gap-2">
        <Lab t="Series">
          <select data-testid="unit-series-select" value={u.node.series || "standard"} onChange={(e) => set(patchAt(d, u.path, { series: e.target.value }))} className={inp}>
            <option value="standard">Standard</option><option value="element">Element</option>
          </select>
        </Lab>
        <Lab t="Unit shape (top)">
          <select data-testid="unit-shape-select" value={u.node.shape_id || ""} onChange={(e) => set(patchAt(d, u.path, { shape_id: e.target.value || null }))} className={inp}>
            <option value="">Rectangle</option>{shapes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Lab>
        <Lab t="Color">
          <input data-testid="unit-color-input" value={u.node.ext_color || ""} onChange={(e) => set(patchAt(d, u.path, { ext_color: e.target.value }))} placeholder={defaultColor()} className={inp} />
        </Lab>
        <Lab t="Interior color">
          <input data-testid="unit-int-color-input" value={u.node.int_color || ""} onChange={(e) => set(patchAt(d, u.path, { int_color: e.target.value }))} placeholder="same as outside" className={inp} />
        </Lab>
      </div>
    </div>
  );
}

export function Designer({ initial, onSave, saveLabel = "Save", busy = false }) {
  const [d, setD] = useState(initial);
  const [sel, setSel] = useState(() => layout(initial).secs[0]?.path || [0]);
  const [msg, setMsg] = useState("");
  const msgTimer = useRef(null);
  const [lib, setLib] = useState({ screens: [], shapes: [] });
  const [tplOpen, setTplOpen] = useState(false);
  const dragStart = useRef(null);
  const price = useDesignPrice(d);
  const [view, setView_] = useState("2d");
  const [gl, setGl] = useState(true);
  const [t3d, setT3d] = useState(0);
  const [saving, setSaving] = useState(false);
  const setView = async (v) => {
    if (v === "3d") { setT3d(performance.now()); setGl(await webgl()); }
    setView_(v);
  };
  const save = async () => {
    setSaving(true);
    const snap = await saveSnapshot(d, shapeMap);
    setSaving(false);
    onSave(d, { ...price, snapshot: snap });
  };
  useEffect(() => {
    Promise.all([api.get("/product-lines/choices"), api.get("/shapes/choices")]).then(([c, s]) => setLib({ screens: c.data.filter((x) => x.category === "screen"), shapes: s.data })).catch(() => {});
  }, []);
  const say = (m) => { setMsg(m); clearTimeout(msgTimer.current); msgTimer.current = setTimeout(() => setMsg(""), 2500); };
  const shapeMap = useMemo(() => Object.fromEntries(lib.shapes.map((s) => [s.id, s])), [lib.shapes]);
  const valid = layout(d).secs.some((s) => s.path.join() === sel.join()) ? sel : layout(d).secs[0].path;
  const onDrag = (dv, delta) => {
    if (!dragStart.current) dragStart.current = { d, dv };
    const base = dragStart.current.d;
    const n = get(base.root, dv.path);
    const L = layout(base);
    const leaves = [...L.units, ...L.secs].filter((r) => r.path.slice(0, dv.path.length).join() === dv.path.join() && r.path.length > dv.path.length);
    const tot = dv.cols ? Math.max(...leaves.map((r) => r.x + r.w)) - Math.min(...leaves.map((r) => r.x)) : Math.max(...leaves.map((r) => r.y + r.h)) - Math.min(...leaves.map((r) => r.y));
    const sizes = (n.sizes?.length === n.kids.length ? n.sizes : n.kids.map(() => tot / n.kids.length)).map(Number);
    const [nd, clamped] = resize(base, dv.path, dv.i, delta, sizes);
    if (clamped) setMsg("Minimum section 12 in");
    setD(nd);
  };
  const setOverall = (k, v, el) => {
    const n = Number(v) || 0;
    if (d.root.t === "mull") {  // assembly: the typed overall must equal the sum of the pieces (else "Sizes don't add up")
      if (n < 12) { if (el) el.value = d[k]; return say("Minimum piece size 12 in"); }
      return setD({ ...d, [k]: n });
    }
    const m = minOf(d);
    const c = Math.max(n, k === "w" ? m.w : m.h);
    if (c !== n) { say("Minimum piece size 12 in"); if (el) el.value = c; }
    setD(scale(d, k === "w" ? c : d.w, k === "h" ? c : d.h));
  };
  const gap = sizeGap(d);
  const onBarMove = (path, i, at) => {
    const n = get(d.root, path);
    setD(patchAt(d, path, { grid: { ...n.grid, bars: n.grid.bars.map((b, j) => (j === i ? { ...b, at } : b)) } }));
  };
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]" data-testid="designer">
      <div className="space-y-3">
        <div className="relative rounded-2xl border border-slate-800 bg-white p-3">
          <ViewToggle view={view} setView={setView} />
          {view === "3d" && !gl && <p data-testid="design-3d-unsupported" className="absolute left-4 top-4 z-10 rounded-full bg-[#D70015] px-3 py-1 text-xs font-semibold text-white">3D isn't supported on this device</p>}
          {view === "3d" && gl ? (
            <div className="h-[52vh] w-full">
              <Suspense fallback={<div data-testid="design-3d-loading" className="grid h-full place-items-center text-sm text-[#6D6D72]">Loading 3D…</div>}>
                <Design3D design={d} shapes={shapeMap} startedAt={t3d} />
              </Suspense>
            </div>
          ) : (
          <DesignCanvas design={d} selected={valid} onSelect={setSel} onDrag={onDrag} onBarMove={onBarMove} onDragEnd={() => { if (dragStart.current) { clearTimeout(msgTimer.current); msgTimer.current = setTimeout(() => setMsg(""), 1500); } dragStart.current = null; }}
            shapes={shapeMap} className="h-[52vh] w-full" />
          )}
          {msg && <p data-testid="design-min-warning" className="absolute left-4 top-4 rounded-full bg-[#0071E3] px-3 py-1 text-xs font-semibold text-white">{msg}</p>}
        </div>
        <PriceBox price={gap && Math.abs(gap) > 0.05 ? null : price} gap={gap} busy={busy || saving} saveLabel={saveLabel} onSave={save} onFit={() => setD(fitPieces(d))}
          stop={stuckSlider(d) ? NO_MOVING : ""} />
      </div>
      <aside className="space-y-5">
        <div className="grid grid-cols-2 gap-2">
          <Lab t="Overall width (in)"><input key={`w${d.w}`} data-testid="design-width-input" inputMode="decimal" defaultValue={d.w} onBlur={(e) => setOverall("w", e.target.value, e.target)} className={inp} /></Lab>
          <Lab t="Overall height (in)"><input key={`h${d.h}`} data-testid="design-height-input" inputMode="decimal" defaultValue={d.h} onBlur={(e) => setOverall("h", e.target.value, e.target)} className={inp} /></Lab>
        </div>
        <SelectedPiece d={d} path={valid} set={setD} onMsg={say} />
        <SectionPanel d={d} path={valid} set={setD} screens={lib.screens} onMsg={say} />
        <UnitPanel d={d} path={valid} set={setD} shapes={lib.shapes} />
        <AddPieces d={d} set={setD} />
        <button data-testid="design-templates-button" onClick={() => setTplOpen(true)} className={btn}>Templates</button>
      </aside>
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle className="font-display text-2xl">Design templates</DialogTitle></DialogHeader>
          <Templates design={d} onLoad={(nd) => { setD(nd); setSel(layout(nd).secs[0].path); setTplOpen(false); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
